import { useQuery } from "@tanstack/react-query";
import { Api } from "../../../services/interceptor";

/** One YouTube video attached to a match from the app (`match_media`). */
export type MatchMedia = { id: number; mediaType: string; url?: string | null; title?: string | null; sortOrder?: number | null };
export type MatchVideo = { id: number; type: string; url: string; title: string; videoId: string };

/** MatchMediaType (backend enum) -> label */
export const MEDIA_LABEL: Record<string, string> = {
  LIVE_STREAM: "Live stream", FULL_MATCH: "Full match", HIGHLIGHTS: "Highlights", INNINGS_HIGHLIGHTS: "Innings highlights", KEY_MOMENTS: "Key moments",
  PRE_MATCH_INTERVIEW: "Pre-match interview", POST_MATCH_INTERVIEW: "Post-match interview", PRESENTATION: "Presentation", ANALYSIS: "Analysis",
};
export const mediaLabel = (type: string, ended: boolean) =>
  type === "LIVE_STREAM" && ended ? "Stream replay" : MEDIA_LABEL[type] ?? type.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

/** YouTube video id from a watch / youtu.be / live / embed / shorts link, or "" when it isn't one. */
export const youtubeId = (raw?: string | null) => {
  try {
    const u = new URL((raw ?? "").trim());
    const host = u.hostname.replace(/^(www|m|music)\./, "");
    if (host === "youtu.be") return u.pathname.slice(1).split("/")[0] ?? "";
    if (host !== "youtube.com" && host !== "youtube-nocookie.com") return "";
    if (u.pathname === "/watch") return u.searchParams.get("v") ?? "";
    const m = u.pathname.match(/^\/(?:live|embed|shorts)\/([\w-]+)/);
    return m ? m[1] : "";
  } catch {
    return "";
  }
};

/**
 * Every video attached to the match (GET /match/{id}/media), in the backend's curated order.
 * The live stream is a LIVE_STREAM row here; the legacy streamURL / youtubeStreamUrl match fields are no longer written.
 */
export const useMatchMedia = (matchId: string) => {
  const q = useQuery({
    queryKey: ["matchMedia", matchId],
    enabled: !!matchId,
    staleTime: 60_000,
    queryFn: async () => ((await Api.get(`match/${matchId}/media`)).data?.data ?? []) as MatchMedia[],
  });
  return (q.data ?? [])
    .filter((m) => (m.url ?? "").trim())
    .map((m): MatchVideo => ({ id: m.id, type: m.mediaType, url: m.url!.trim(), title: (m.title ?? "").trim(), videoId: youtubeId(m.url) }));
};
